import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider, useAuth, ProtectedRoute, MODULES } from '@/contexts/AuthContext';
import EmployeesPage from '@/pages/parametrizacion/employees/EmployeesPage';
import ServicesPage from '@/pages/parametrizacion/services/ServicesPage';
import GarmentTypesPage from './pages/parametrizacion/garmentTypes/GarmentTypesPage';
import LoginPage from '@/pages/LoginPage';
import UnauthorizedPage from '@/pages/UnauthorizedPage';
import UsuariosPage from '@/pages/parametrizacion/usuarios/UsuariosPage';
import WeeklyOverviewPage from '@/pages/shiftManagement/WeeklyOverviewPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ChangePasswordPage from '@/pages/profile/ChangePasswordPage';
import EmployeeDocumentsPage from '@/pages/documents/EmployeeDocumentsPage';
import DocumentationHomePage from '@/pages/documents/DocumentationHomePage';
import EmployeesDocsHome from '@/pages/documents/EmployeesDocsHome';
import DocumentTypesPage from '@/pages/parametrizacion/documentTypes/DocumentTypesPage';
import PlantasPage from '@/pages/parametrizacion/plantas/PlantasPage';
import AreasPage from '@/pages/parametrizacion/areas/AreasPage';
import CentrosCostoPage from '@/pages/parametrizacion/centrosCosto/CentrosCostoPage';
import PlantasListPage from '@/pages/plantas/PlantasListPage';
import PlantaDashboard from '@/pages/plantas/PlantaDashboard';
import PlantaInventario from '@/pages/plantas/PlantaInventario';
import UtilityServicesPage from '@/pages/utilityServices/UtilityServicesPage';
import DepositoPage from '@/pages/deposito/DepositoPage';
import DashboardPage from '@/pages/DashboardPage';
import AutoServicioPage from '@/pages/autoservicio/AutoServicioPage';

// Utilidad: detectar si el usuario es operario/operador
const isOperarioUser = (user) => {
  if (!user) return false;
  // roles en array
  if (Array.isArray(user.roles)) {
    const roles = user.roles.map((r) => String(r || '').toLowerCase().trim());
    if (roles.includes('operario') || roles.includes('operador')) return true;
  }
  // rol/role en string
  const primary = String(user.rol ?? user.role ?? '').toLowerCase().trim();
  return primary === 'operario' || primary === 'operador';
};

// Componente para rutas públicas que redirigen si el usuario ya está autenticado
const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return children;
  const isOperario = isOperarioUser(user);
  return <Navigate to={isOperario ? '/operario-dashboard' : '/dashboard'} replace />;
};

// Landing por defecto según rol
const DefaultLanding = () => {
  const { user } = useAuth();
  const isOperario = isOperarioUser(user);
  return <Navigate to={isOperario ? '/operario-dashboard' : '/dashboard'} replace />;
};

function AppContent() {
  return (
    <Layout>
      <Routes>
        {/* Rutas públicas */}
        <Route path="/login" element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } />
        <Route path="/forgot-password" element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        } />
        <Route path="/reset-password" element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        } />

        {/* Ruta raíz redirige según rol si autenticado, sino a login */}
        <Route path="/" element={
          <ProtectedRoute>
            <DefaultLanding />
          </ProtectedRoute>
        } />

        {/* Rutas protegidas */}
        <Route path="/dashboard" element={
          <ProtectedRoute requiredModule="dashboard">
            <DashboardPage />
          </ProtectedRoute>
        } />


        {/* Legacy/alias route redirects to Liquidación */}
        <Route path="/billing" element={<Navigate to="/liquidacion" replace />} />


        {/* Parametrización */}
        <Route path="/parametrizacion/empleados" element={
          <ProtectedRoute requiredModule="employees">
            <EmployeesPage />
          </ProtectedRoute>
        } />
        <Route path="/parametrizacion/tipos-archivo" element={
          <ProtectedRoute requiredModule="document_types">
            <DocumentTypesPage />
          </ProtectedRoute>
        } />
        <Route path="/parametrizacion/tipos-prenda" element={
          <ProtectedRoute requiredModule="garment_types">
            <GarmentTypesPage />
          </ProtectedRoute>
        } />

        <Route path="/parametrizacion/servicios" element={
          <ProtectedRoute requiredModule="services">
            <ServicesPage />
          </ProtectedRoute>
        } />
        <Route path="/parametrizacion/usuarios" element={
          <ProtectedRoute requiredModule="users">
            <UsuariosPage />
          </ProtectedRoute>
        } />
        <Route path="/parametrizacion/areas" element={
          <ProtectedRoute requiredModule="areas">
            <AreasPage />
          </ProtectedRoute>
        } />
        <Route path="/parametrizacion/centros-costo" element={
          <ProtectedRoute requiredModule="centros_costo">
            <CentrosCostoPage />
          </ProtectedRoute>
        } />
        <Route path="/parametrizacion/plantas" element={
          <ProtectedRoute requiredModule="plantas">
            <PlantasPage />
          </ProtectedRoute>
        } />


        {/* Plantas */}
        <Route path="/plantas" element={
          <ProtectedRoute requiredModule="plantas">
            <PlantasListPage />
          </ProtectedRoute>
        } />
        <Route path="/plantas/:idPlanta" element={
          <ProtectedRoute requiredModule="plantas">
            <PlantaDashboard />
          </ProtectedRoute>
        } />
        <Route path="/plantas/:idPlanta/inventario" element={
          <ProtectedRoute requiredModule="plantas">
            <PlantaInventario />
          </ProtectedRoute>
        } />
        <Route path="/plantas/:idPlanta/servicios-publicos" element={
          <ProtectedRoute requiredModule="plantas">
            <UtilityServicesPage />
          </ProtectedRoute>
        } />
        <Route path="/plantas/:idPlanta/deposito-ropa" element={
          <ProtectedRoute requiredModule="plantas">
            <DepositoPage />
          </ProtectedRoute>
        } />

        {/* AutoServicio */}
        <Route path="/autoservicio" element={
          <ProtectedRoute requiredModule="autoservicio">
            <AutoServicioPage />
          </ProtectedRoute>
        } />

        {/* Reportes */}
        <Route path="/reportes" element={<Navigate to="/dashboard" replace />} />

        {/* Documentos */}
        <Route path="/documentacion" element={
          <ProtectedRoute requiredModule={MODULES.DOCUMENTATION}>
            <DocumentationHomePage />
          </ProtectedRoute>
        } />
        <Route path="/documentacion/empleados" element={
          <ProtectedRoute requiredModule={MODULES.DOCUMENTATION}>
            <EmployeesDocsHome />
          </ProtectedRoute>
        } />
        <Route path="/documentacion/empleados/:idEmpleado" element={
          <ProtectedRoute requiredModule="employees">
            <EmployeeDocumentsPage />
          </ProtectedRoute>
        } />

        <Route path="/parametrizacion/empleados/:idEmpleado/documentos" element={
          <ProtectedRoute requiredModule="employees">
            <EmployeeDocumentsPage />
          </ProtectedRoute>
        } />

        <Route path="/supervision/turnos" element={
          <ProtectedRoute requiredModule="workplaces">
            <WeeklyOverviewPage />
          </ProtectedRoute>
        } />


        {/* Perfil */}
        <Route path="/perfil/cambiar-contrasena" element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        } />

        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* Ruta por defecto para páginas no encontradas */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
        <Toaster />
      </AuthProvider>
    </Router>
  );
}

export default App;